import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FiDownload, FiPrinter } from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { useAuth } from '../../context/AuthContext';
import useCurrency from '../../hooks/useCurrency';
import PageLayout from '../../components/PageLayout';
import {
  FiltersCard, FilterGrid, FilterItem, FilterLabel, FilterSelect,
  KPIGrid, KPI, KL, KV, ToolbarRow, ExportBtn, ReportCard, RTable, Loading, Empty, Pill,
} from './reportStyles';
import { loadAssetReportData, exportRowsToExcel, printAssetReport } from '../../utils/assetReportHelper';
import { appreciationOf } from '../../utils/depreciationHelper';

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const TYPE_LABEL = { sold: 'Sold', scrapped: 'Scrapped', written_off: 'Written Off' };

const AssetDisposalsReport = () => {
  const { activeCompany, activeBranch, activeFinancialYear } = useAuth();
  const { formatCurrency } = useCurrency();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState('fy');

  const load = useCallback(async () => {
    if (!activeCompany?.id) return;
    setLoading(true);
    try {
      const d = await loadAssetReportData(activeCompany.id, activeBranch);
      setAssets(d.assets);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [activeCompany, activeBranch]);
  useEffect(() => { load(); }, [load]);

  const fyStart = scope === 'fy' ? (activeFinancialYear?.startDate || null) : null;
  const fyEnd = scope === 'fy' ? (activeFinancialYear?.endDate || null) : null;

  const rows = useMemo(() => assets
    .filter(a => a.status === 'Disposed')
    .filter(a => {
      if (!fyStart && !fyEnd) return true;
      const d = a.disposalDate;
      if (fyStart && (!d || d < fyStart)) return false;
      if (fyEnd && (!d || d > fyEnd)) return false;
      return true;
    })
    .map(a => {
      const cost = round2(a.acquisitionCost);
      const appr = appreciationOf(a); // Asset Appreciation (0 for most assets)
      const accum = round2(a.accumulatedDepreciation);
      const nbv = round2(cost + appr - accum);
      const proceeds = round2(a.disposalProceeds);
      const gainLoss = a.gainLossAmount != null ? round2(a.gainLossAmount) : round2(proceeds - nbv);
      return { a, cost, appr, accum, nbv, proceeds, gainLoss };
    })
    .sort((x, y) => (y.a.disposalDate || '').localeCompare(x.a.disposalDate || '')), [assets, fyStart, fyEnd]);

  const showAppr = useMemo(() => rows.some(r => r.appr > 0), [rows]);
  const totals = useMemo(() => rows.reduce((t, r) => ({
    cost: round2(t.cost + r.cost), appr: round2(t.appr + r.appr), nbv: round2(t.nbv + r.nbv), proceeds: round2(t.proceeds + r.proceeds),
    gain: round2(t.gain + (r.gainLoss > 0 ? r.gainLoss : 0)), loss: round2(t.loss + (r.gainLoss < 0 ? -r.gainLoss : 0)),
  }), { cost: 0, appr: 0, nbv: 0, proceeds: 0, gain: 0, loss: 0 }), [rows]);
  const netGainLoss = round2(totals.gain - totals.loss);

  const columns = [
    { key: 'Code', label: 'Code' }, { key: 'Asset', label: 'Asset' }, { key: 'Date', label: 'Disposal Date' },
    { key: 'Type', label: 'Type' }, { key: 'Cost', label: 'Cost', r: true },
    ...(showAppr ? [{ key: 'Appreciation', label: 'Appreciation', r: true }] : []),
    { key: 'AccumDep', label: 'Accum. Dep.', r: true },
    { key: 'NBV', label: 'NBV', r: true }, { key: 'Proceeds', label: 'Proceeds', r: true }, { key: 'GainLoss', label: 'Gain / (Loss)', r: true },
  ];
  const buildRows = () => rows.map(r => ({
    Code: r.a.assetCode, Asset: r.a.assetName, Date: r.a.disposalDate || '', Type: TYPE_LABEL[r.a.disposalType] || r.a.disposalType,
    Cost: formatCurrency(r.cost), ...(showAppr ? { Appreciation: formatCurrency(r.appr) } : {}), AccumDep: formatCurrency(r.accum), NBV: formatCurrency(r.nbv),
    Proceeds: formatCurrency(r.proceeds), GainLoss: formatCurrency(r.gainLoss),
  }));
  const scopeLabel = scope === 'fy' ? `FY ${fyStart || ''} → ${fyEnd || ''}` : 'All time';

  const onPrint = () => printAssetReport({
    company: activeCompany, branch: activeBranch && activeBranch.id !== 'all' ? activeBranch : null,
    title: 'Asset Disposals', filters: scopeLabel, columns, rows: buildRows(),
    totals: { Cost: formatCurrency(totals.cost), ...(showAppr ? { Appreciation: formatCurrency(totals.appr) } : {}), NBV: formatCurrency(totals.nbv), Proceeds: formatCurrency(totals.proceeds), GainLoss: formatCurrency(netGainLoss) },
    kpis: [
      { label: 'Disposals', value: String(rows.length) },
      { label: 'Proceeds', value: formatCurrency(totals.proceeds) },
      { label: 'Net Gain / (Loss)', value: formatCurrency(netGainLoss), highlight: true },
    ],
  });

  return (
    <PageLayout pageTitle="Asset Disposals" breadcrumb="Reports / Fixed Assets / Disposals">
      <FiltersCard>
        <FilterGrid>
          <FilterItem>
            <FilterLabel>Period</FilterLabel>
            <FilterSelect value={scope} onChange={(e) => setScope(e.target.value)}>
              <option value="fy">Active Financial Year</option>
              <option value="all">All time</option>
            </FilterSelect>
          </FilterItem>
        </FilterGrid>
      </FiltersCard>

      <KPIGrid>
        <KPI><KL>Disposals</KL><KV>{rows.length}</KV></KPI>
        <KPI><KL>Proceeds</KL><KV>{formatCurrency(totals.proceeds)}</KV></KPI>
        <KPI><KL>Gain</KL><KV style={{ color: colors.success.main }}>{formatCurrency(totals.gain)}</KV></KPI>
        <KPI $highlight><KL>Net Gain / (Loss)</KL><KV>{formatCurrency(netGainLoss)}</KV></KPI>
      </KPIGrid>

      <ToolbarRow>
        <h3 style={{ color: colors.text.secondary }}>{scopeLabel}</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <ExportBtn onClick={() => exportRowsToExcel('AssetDisposals', 'Disposals', buildRows())}><FiDownload /> Excel</ExportBtn>
          <ExportBtn onClick={onPrint}><FiPrinter /> Print</ExportBtn>
        </div>
      </ToolbarRow>

      {loading ? <Loading>Loading...</Loading> : rows.length === 0 ? <Empty>No disposals for this period.</Empty> : (
        <ReportCard>
          <RTable>
            <thead>
              <tr>
                <th>Code</th><th>Asset</th><th>Disposal Date</th><th>Type</th>
                <th className="r">Cost</th>{showAppr && <th className="r">Appreciation</th>}<th className="r">Accum. Dep.</th><th className="r">NBV</th>
                <th className="r">Proceeds</th><th className="r">Gain / (Loss)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.a.id}>
                  <td><strong>{r.a.assetCode}</strong></td>
                  <td>{r.a.assetName}</td>
                  <td>{r.a.disposalDate}</td>
                  <td><Pill $color={colors.text.disabled}>{TYPE_LABEL[r.a.disposalType] || r.a.disposalType}</Pill></td>
                  <td className="r">{formatCurrency(r.cost)}</td>
                  {showAppr && <td className="r">{formatCurrency(r.appr)}</td>}
                  <td className="r">{formatCurrency(r.accum)}</td>
                  <td className="r">{formatCurrency(r.nbv)}</td>
                  <td className="r">{formatCurrency(r.proceeds)}</td>
                  <td className="r" style={{ color: r.gainLoss >= 0 ? colors.success.main : colors.error.main }}>{formatCurrency(r.gainLoss)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={4}>Total</td>
                <td className="r">{formatCurrency(totals.cost)}</td>
                {showAppr && <td className="r">{formatCurrency(totals.appr)}</td>}
                <td className="r"></td>
                <td className="r">{formatCurrency(totals.nbv)}</td>
                <td className="r">{formatCurrency(totals.proceeds)}</td>
                <td className="r">{formatCurrency(netGainLoss)}</td>
              </tr>
            </tfoot>
          </RTable>
        </ReportCard>
      )}
    </PageLayout>
  );
};

export default AssetDisposalsReport;
